// apps/codeframe/src/chrome/timeline/timeMap.ts
import type { MotionProp } from '../../store/types';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Map a time (ms) to an x offset (px) within a track of `width` px. */
export function timeToX(t: number, duration: number, width: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return clamp(t, 0, duration) * (width / duration);
}

/** Inverse of timeToX: an x offset (px) back to a time (ms), clamped. */
export function xToTime(x: number, duration: number, width: number): number {
  if (!Number.isFinite(duration) || duration <= 0 || width <= 0) return 0;
  return Math.round(clamp(x, 0, width) * (duration / width));
}

/** Snap a time to the nearest fps frame boundary; identity if no fps grid. */
export function snapTime(t: number, fps?: number): number {
  if (!fps || fps <= 0) return Math.round(t);
  const frameMs = 1000 / fps;
  return Math.round(Math.round(t / frameMs) * frameMs);
}

export interface RulerTick {
  t: number;
  x: number;
  major: boolean;
}

/**
 * Generate ruler ticks across [0,duration]. Picks a "nice" interval so ~6-12
 * ticks span the width; every 5th tick is major (labelled).
 */
export function rulerTicks(duration: number, width: number): RulerTick[] {
  if (!Number.isFinite(duration) || duration <= 0 || width <= 0) return [{ t: 0, x: 0, major: true }];
  const targetCount = Math.max(4, Math.min(12, Math.round(width / 80)));
  const raw = duration / targetCount;
  // round raw up to a nice 1/2/5 * 10^n step
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  const ticks: RulerTick[] = [];
  let i = 0;
  for (let t = 0; t <= duration + 0.5; t += step, i++) {
    ticks.push({ t, x: timeToX(t, duration, width), major: i % 5 === 0 });
  }
  return ticks;
}

const ONE_VALUED: MotionProp[] = ['opacity', 'scale', 'scaleX', 'scaleY'];

/** Neutral starting value for a new keyframe on a given prop. */
export function defaultValueForProp(prop: MotionProp): number | string {
  if (prop === 'color') return '#000000';
  if (prop === 'backgroundColor') return '#ffffff';
  return ONE_VALUED.includes(prop) ? 1 : 0;
}
